import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GET_BROADCAST_LIVE,
  GetBroadcastLiveData,
  GetBroadcastLiveVariables,
} from '@jwp/ott-common/src/graphql/query/broadcastLive.queries';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import { PATH_BROADCAST_LIVE_DETAILS, PATH_USER_VIDEOS } from '@jwp/ott-common/src/paths';
import { getBroadcastVodSourceUrl, isBroadcastLiveEnded } from '@jwp/ott-common/src/utils/broadcastLive';

import useQueryParam from '../../hooks/useQueryParam';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import VideoPreview from '../../components/VideoPreview/VideoPreview';

import styles from './BroadcastLivePlayPage.module.scss';

const BroadcastLivePlayPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation(['video', 'common']);
  const navigate = useNavigate();
  const urlReturnPath = useQueryParam('returnPath');

  const { data, loading, error } = useQuery<GetBroadcastLiveData, GetBroadcastLiveVariables>(GET_BROADCAST_LIVE, {
    client: getAuthApolloClient(),
    variables: { blId: id || '' },
    skip: !id,
    errorPolicy: 'all',
  });

  const stream = data?.getBroadcastLive?.broadcastLive;
  const hlsUrl = stream ? getBroadcastVodSourceUrl(stream) : '';

  useEffect(() => {
    if (!stream || loading) { return; }

    if (!isBroadcastLiveEnded(stream.blEndTime)) {
      navigate(
        `${PATH_BROADCAST_LIVE_DETAILS}/${stream.blId}${urlReturnPath ? `?returnPath=${encodeURIComponent(urlReturnPath)}` : ''}`,
        { replace: true },
      );
    }
  }, [stream, loading, navigate, urlReturnPath]);

  const handleClose = () => {
    if (urlReturnPath) {
      navigate(urlReturnPath);
      return;
    }
    navigate(`${PATH_USER_VIDEOS}?tab=broadcasts`);
  };

  if (loading) { return <LoadingOverlay />; }

  if (error || !stream) {
    return <div className={styles.errorState}>{t('video:broadcast_live.load_error')}</div>;
  }

  if (!isBroadcastLiveEnded(stream.blEndTime)) {
    return <LoadingOverlay />;
  }

  if (!hlsUrl) {
    return <div className={styles.errorState}>{t('video:broadcast_live.streaming_not_available')}</div>;
  }

  return (
    <VideoPreview
      previewUrl={hlsUrl}
      title={stream.blTitle}
      description={stream.blDescription || undefined}
      onClose={handleClose}
      showDetails
      isReel={false}
      item={{
        mediaid: stream.blJwStreamId || stream.blId,
        title: stream.blTitle,
        description: stream.blDescription,
      } as any}
      reconnectingLabel={t('video:reconnecting', { defaultValue: 'Reconnecting...' })}
    />
  );
};

export default BroadcastLivePlayPage;
